export type EquipmentImageKind = 'radio' | 'antenna';

export interface EquipmentImageManifestEntry {
  readonly kind: EquipmentImageKind;
  readonly sku: string;
  readonly src: string;
  readonly source: 'tech-specs' | 'uisp-design-center';
}

/**
 * Product images were retrieved on 2026-08-18 from official Ubiquiti sources.
 * Only exact canonical SKU matches already present in the built-in Ubiquiti
 * catalog are listed; model-name guessing is forbidden. Four older family-card
 * images are retained as audited provenance assets but are no longer mapped to
 * selectable equipment because those family identifiers are not exact SKUs.
 *
 * The downloaded sources were transparent PNG responses from
 * images.svc.ui.com. Each response was checked for PNG magic, decoded size,
 * alpha support, a 2 MiB limit, and SHA-256 uniqueness before it was accepted.
 * Ubiquiti publishes identical Tech Specs pixels for AF-5/AF-5U and for the
 * two AF11-Complete band variants, so those pairs intentionally share one local
 * file instead of storing duplicate binaries. Tech Specs no longer lists the
 * archived AF-5X and RocketM5. Their exact images come from the official UISP
 * Design Center static bundle and are recorded separately below.
 */
export const equipmentImageProvenance = Object.freeze({
  owner: 'Ubiquiti Inc.',
  sourceName: 'Official Ubiquiti Tech Specs product cards',
  sourceIndexUrls: [
    'https://techspecs.ui.com/uisp/wireless?subcategory=all-wireless',
    'https://techspecs.ui.com/uisp/60ghz-wireless',
  ] as const,
  sourceImageHost: 'images.svc.ui.com',
  archivedProductSources: [
    {
      kind: 'radio',
      sku: 'AF-5X',
      sourceUrl: 'https://ispdesign.ui.com/static/media/af06.f6a694a0.png',
      sha256: '27c102eb7c462cf01dd5dc7c9084d11dc7e4bde52e7f564b2bced24299b46d1c',
    },
    {
      kind: 'radio',
      sku: 'RocketM5',
      sourceUrl: 'https://ispdesign.ui.com/static/media/e105.a76cf9b9.png',
      sha256: '5b9768cc4a91463a06651b8daa3300240a3d822888978586335add1869f65b46',
    },
  ] as const,
  retrievedOn: '2026-08-18',
  matchPolicy: 'exact-kind-and-canonical-sku',
  uniqueLocalFiles: 90,
  exactSkuMappings: 88,
  retiredFamilyAssets: [
    'am-5g1.png', 'am-5g2.png', 'horn-5.png', 'rd-5g.png',
  ] as const,
});

const exactRadioSkus = [
  'AF-11', 'AF-24', 'AF-24HD', 'AF-2X', 'AF-5', 'AF-5U', 'AF-5X', 'AF-5XHD',
  'AF11-Complete-HB', 'AF11-Complete-LB', 'AF60-HD', 'AF60-LR', 'AF60-XG',
  'AF60-XR', 'B-DB-AC', 'BulletAC-IP67', 'BulletM2-HP', 'GBE', 'GBE-LR',
  'GBE-Plus', 'IS-5AC', 'IS-M5', 'LAP-120', 'LAP-GPS', 'LBE-5AC-GEN2',
  'LBE-5AC-LR', 'LBE-5AC-XR', 'LBE-M5-23', 'Loco5AC', 'LocoM2', 'LocoM5',
  'LTU-Instant', 'LTU-Lite', 'LTU-LR', 'LTU-PRO', 'LTU-Rocket', 'LTU-XR',
  'NBE-2AC-13', 'NBE-5AC-GEN2', 'NBE-M5-16', 'NS-5AC', 'NSM2', 'NSM5',
  'PBE-2AC-400', 'PBE-5AC-500', 'PBE-5AC-620', 'PBE-5AC-Gen2',
  'PBE-5AC-ISO-Gen2', 'PBE-M2-400', 'PBE-M5-300', 'PBE-M5-300-ISO',
  'PBE-M5-400', 'PBE-M5-400-ISO', 'PS-5AC', 'R2AC-PRISM', 'R5AC-LITE',
  'RocketM3', 'RocketM5', 'RP-5AC-Gen2', 'Wave-AP', 'Wave-AP-Gen2', 'Wave-AP-Micro',
  'Wave-LR', 'Wave-MLO5', 'Wave-MLO6', 'Wave-Nano', 'Wave-Pico', 'Wave-Pro',
] as const;

const exactAntennaSkus = [
  'AF-5G23-S45', 'AF-5G30-S45', 'AF-5G34-S45', 'AM-2G15-120',
  'AM-2G16-90', 'AM-5AC21-60', 'AM-5AC22-45',
  'AM-9M13-120', 'AM-V2G-Ti', 'AMO-2G10', 'AMO-2G13', 'AMO-5G10',
  'AMO-5G13', 'AMY-9M16x2', 'AP-5AC-90-HD',
  'RD-5G30-LW', 'RD-5G31-AC', 'UISP-Dish', 'UISP-Dish-Mini', 'UISP-Horn',
] as const;

const sharedFileAliases: Readonly<Record<string, string>> = Object.freeze({
  'radio:AF-5U': 'af-5',
  'radio:AF11-Complete-LB': 'af11-complete-hb',
});

function skuAssetSlug(sku: string): string {
  return sku.toLocaleLowerCase('en-US').replace(/[^a-z0-9]+/gu, '-').replace(/^-|-$/gu, '');
}

function entriesFor(kind: EquipmentImageKind, skus: readonly string[]): EquipmentImageManifestEntry[] {
  return skus.map((sku) => {
    const slug = sharedFileAliases[`${kind}:${sku}`] ?? skuAssetSlug(sku);
    const source = kind === 'radio' && (sku === 'AF-5X' || sku === 'RocketM5')
      ? 'uisp-design-center'
      : 'tech-specs';
    return Object.freeze({ kind, sku, src: `/assets/equipment/ubiquiti/${slug}.png`, source });
  });
}

export const equipmentImageManifest: readonly EquipmentImageManifestEntry[] = Object.freeze([
  ...entriesFor('radio', exactRadioSkus),
  ...entriesFor('antenna', exactAntennaSkus),
]);

const imageByKindAndSku = new Map(
  equipmentImageManifest.map((entry) => [`${entry.kind}:${entry.sku}`, entry.src] as const),
);

export function resolveEquipmentImage(
  kind: EquipmentImageKind,
  item: { sku: string | null; model: string },
): string | null {
  if (!item.sku) return null;
  return imageByKindAndSku.get(`${kind}:${item.sku}`) ?? null;
}
